import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import DashboardLayout from '../components/DashboardLayout';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useTheme } from '../contexts/ThemeContext';

const API = process.env.REACT_APP_API_URL || '/api';
const getToken = () => localStorage.getItem('token');
const COLORS = ['#4E9AF1','#2DC98A','#FFB347','#FF6B6B','#A78BFA','#22D3EE','#F472B6','#86EFAC'];

const fmt = (v, type='num') => {
  if (v===null||v===undefined||v==='') return '—';
  const n = parseFloat(v); if (isNaN(n)) return '—';
  if (type==='usd') { if(n>=1000000) return '$'+(n/1000000).toFixed(1)+'M'; if(n>=1000) return '$'+(n/1000).toFixed(1)+'K'; return '$'+n.toFixed(2); }
  if (type==='pct') return n.toFixed(2)+'%';
  if (type==='dec') return n.toFixed(2);
  if(n>=1000000) return (n/1000000).toFixed(1)+'M';
  if(n>=1000) return (n/1000).toFixed(1)+'K';
  return n.toLocaleString();
};

const getDates = (range) => {
  const now = new Date();
  let dateTo = now.toISOString().split('T')[0], dateFrom;
  if (range==='7d') dateFrom = new Date(now-7*86400000).toISOString().split('T')[0];
  else if (range==='90d') dateFrom = new Date(now-90*86400000).toISOString().split('T')[0];
  else if (range==='this_month') dateFrom = new Date(now.getFullYear(),now.getMonth(),1).toISOString().split('T')[0];
  else if (range==='last_month') { dateFrom=new Date(now.getFullYear(),now.getMonth()-1,1).toISOString().split('T')[0]; dateTo=new Date(now.getFullYear(),now.getMonth(),0).toISOString().split('T')[0]; }
  else dateFrom = new Date(now-30*86400000).toISOString().split('T')[0];
  return { dateFrom, dateTo };
};

const DATE_OPTIONS = [
  {value:'7d',label:'Últimos 7 días'},
  {value:'30d',label:'Últimos 30 días'},
  {value:'90d',label:'Últimos 90 días'},
  {value:'this_month',label:'Este mes'},
  {value:'last_month',label:'Mes anterior'},
];

const TICK = {fontSize:10,fill:'#94a3b8'};
const GRID = '#f0f4f8';
const TIP = {borderRadius:8,fontSize:12,border:'1px solid #e2e8f0',background:'#fff'};
const fmtTick = v => v>=1000000?(v/1000000).toFixed(1)+'M':v>=1000?(v/1000).toFixed(0)+'K':String(v);

function Card({children,style}) {
  return <div style={{background:'#fff',borderRadius:12,border:'1px solid #eef2f7',padding:20,marginBottom:16,...style}}>{children}</div>;
}

function KpiGrid({kpis}) {
  return (
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:12,marginBottom:20}}>
      {kpis.map((k,i) => (
        <div key={i} style={{background:'#fff',borderRadius:12,padding:'16px 18px',border:'1px solid #eef2f7',borderTop:`3px solid ${k.color||'#4E9AF1'}`}}>
          <div style={{fontSize:11,fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:6}}>{k.label}</div>
          <div style={{fontSize:24,fontWeight:900,color:'#0f172a'}}>{fmt(k.value,k.type)}</div>
          {k.sub && <div style={{fontSize:11,color:'#94a3b8',marginTop:4}}>{k.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function TrendChart({data,lines,title,height=200}) {
  if (!data||data.length===0) return null;
  return (
    <Card>
      {title && <div style={{fontWeight:700,fontSize:13,color:'#1e293b',marginBottom:12}}>{title}</div>}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{top:4,right:4,bottom:0,left:0}}>
          <defs>
            {lines.map((l,i) => (
              <linearGradient key={i} id={`g${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={l.color} stopOpacity={0.2}/>
                <stop offset="95%" stopColor={l.color} stopOpacity={0}/>
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false}/>
          <XAxis dataKey="date_label" tick={TICK} axisLine={false} tickLine={false}/>
          <YAxis tick={TICK} axisLine={false} tickLine={false} width={40} tickFormatter={fmtTick}/>
          <Tooltip contentStyle={TIP}/>
          <Legend wrapperStyle={{fontSize:12}}/>
          {lines.map((l,i) => (
            <Area key={i} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} strokeWidth={2.5} fill={`url(#g${i})`} dot={false}/>
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );
}

function CampaignTable({campaigns,color}) {
  if (!campaigns?.length) return <div style={{padding:32,textAlign:'center',color:'#94a3b8',fontSize:13}}>Sin campañas en este período</div>;
  const cols = [{k:'campaign_name',l:'Campaña',t:'str'},{k:'spent',l:'Gasto',t:'usd'},{k:'impressions',l:'Impresiones',t:'num'},{k:'clicks',l:'Clicks',t:'num'},{k:'ctr',l:'CTR',t:'pct'},{k:'cpc',l:'CPC',t:'usd'},{k:'conversions',l:'Conv.',t:'num'}];
  return (
    <div style={{overflowX:'auto'}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
        <thead><tr style={{background:color+'10',borderBottom:`2px solid ${color}20`}}>
          {cols.map(c=><th key={c.k} style={{padding:'10px 14px',textAlign:c.t==='str'?'left':'right',fontSize:11,fontWeight:700,color,textTransform:'uppercase',letterSpacing:'0.06em',whiteSpace:'nowrap'}}>{c.l}</th>)}
        </tr></thead>
        <tbody>
          {campaigns.slice(0,20).map((row,i)=>(
            <tr key={i} style={{background:i%2===0?'#fff':'#f8fafc',borderBottom:'1px solid #f1f5f9'}}>
              {cols.map(c=><td key={c.k} style={{padding:'9px 14px',textAlign:c.t==='str'?'left':'right',color:c.t==='str'?'#1e293b':'#475569',fontWeight:c.t==='str'?600:400,maxWidth:c.t==='str'?220:undefined,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                {c.t==='str'?(row[c.k]||'—'):fmt(row[c.k],c.t)}
              </td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Tabs({tabs,active,onSelect}) {
  return (
    <div style={{display:'flex',gap:2,marginBottom:20,borderBottom:'2px solid #f1f5f9'}}>
      {tabs.map(t=>(
        <button key={t.id} onClick={()=>onSelect(t.id)} style={{
          padding:'9px 16px',fontSize:13,fontWeight:600,cursor:'pointer',background:'none',border:'none',
          borderBottom:active===t.id?`2px solid ${t.color}`:'2px solid transparent',
          color:active===t.id?t.color:'#64748b',marginBottom:-2,transition:'all 0.15s'
        }}>{t.label}</button>
      ))}
    </div>
  );
}

function SectionHeader({title,subtitle,color,icon}) {
  return (
    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:20}}>
      <div style={{width:34,height:34,borderRadius:9,background:color+'15',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16}}>{icon}</div>
      <div>
        <div style={{fontSize:16,fontWeight:800,color:'#0f172a'}}>{title}</div>
        {subtitle&&<div style={{fontSize:12,color:'#94a3b8',marginTop:1}}>{subtitle}</div>}
      </div>
    </div>
  );
}

function NoData({msg}) {
  return <Card><div style={{textAlign:'center',color:'#94a3b8',padding:40,fontSize:13}}>{msg||'Sin datos para este período'}</div></Card>;
}

// ── PAID MEDIA ────────────────────────────────────────────────────────────────
function PaidMediaReport({clientId,dateRange}) {
  const [meta,setMeta]=useState(null);
  const [google,setGoogle]=useState(null);
  const [metaCamps,setMetaCamps]=useState([]);
  const [googleCamps,setGoogleCamps]=useState([]);
  const [metaTrend,setMetaTrend]=useState([]);
  const [googleTrend,setGoogleTrend]=useState([]);
  const [loading,setLoading]=useState(true);
  const [tab,setTab]=useState('meta');

  useEffect(()=>{
    if(!clientId)return;
    setLoading(true);
    const h={Authorization:`Bearer ${getToken()}`};
    const {dateFrom,dateTo}=getDates(dateRange);
    const rangeParam=dateRange;
    const metaEp=(dateRange==='this_month'||dateRange==='last_month')
      ?`${API}/campaigns?client_id=${clientId}&platform=meta&date_from=${dateFrom}&date_to=${dateTo}`
      :`${API}/meta/campaigns-live?client_id=${clientId}&date_from=${dateFrom}&date_to=${dateTo}`;
    Promise.all([
      axios.get(metaEp,{headers:h}).catch(()=>({data:null})),
      axios.get(`${API}/campaigns?client_id=${clientId}&platform=google&date_from=${dateFrom}&date_to=${dateTo}`,{headers:h}).catch(()=>({data:[]})),
      axios.get(`${API}/analytics/daily-trend?client_id=${clientId}&platform=meta&range=${rangeParam}`,{headers:h}).catch(()=>({data:{days:[]}})),
      axios.get(`${API}/analytics/daily-trend?client_id=${clientId}&platform=google&range=${rangeParam}`,{headers:h}).catch(()=>({data:{days:[]}})),
    ]).then(([mRes,gRes,mTrend,gTrend])=>{
      const mc=Array.isArray(mRes.data)?mRes.data:(mRes.data?.campaigns||[]);
      const gc=Array.isArray(gRes.data)?gRes.data:(gRes.data?.campaigns||[]);
      const sum=(a,k)=>a.reduce((s,c)=>s+(parseFloat(c[k])||0),0);
      const avg=(a,k)=>a.length?sum(a,k)/a.length:0;
      if(mc.length){
        const norm=mc.map(c=>({...c,campaign_name:c.name||c.campaign_name||'—'}));
        setMeta({spent:sum(norm,'spent'),impressions:sum(norm,'impressions'),clicks:sum(norm,'clicks'),conversions:sum(norm,'conversions'),ctr:avg(norm,'ctr'),cpc:avg(norm,'cpc'),reach:sum(norm,'reach')});
        setMetaCamps(norm.sort((a,b)=>(parseFloat(b.spent)||0)-(parseFloat(a.spent)||0)));
      }
      if(gc.length){
        const gnorm=gc.map(c=>({...c,campaign_name:c.name||c.campaign_name||'—'}));
        setGoogle({spent:sum(gnorm,'spent'),impressions:sum(gnorm,'impressions'),clicks:sum(gnorm,'clicks'),conversions:sum(gnorm,'conversions'),ctr:avg(gnorm,'ctr'),cpc:avg(gnorm,'cpc')});
        setGoogleCamps(gnorm.sort((a,b)=>(parseFloat(b.spent)||0)-(parseFloat(a.spent)||0)));
      }
      setMetaTrend(mTrend.data?.days||[]);
      setGoogleTrend(gTrend.data?.days||[]);
    }).finally(()=>setLoading(false));
  },[clientId,dateRange]);

  const [ads,setAds]=useState([]);
  const [adsLoading,setAdsLoading]=useState(false);
  const [viewMode,setViewMode]=useState('table'); // 'table' | 'cards'

  useEffect(()=>{
    if(!clientId||tab!=='creatives')return;
    setAdsLoading(true);
    const h={Authorization:`Bearer ${getToken()}`};
    const {dateFrom,dateTo}=getDates(dateRange);
    axios.get(`${API}/campaigns/creatives-with-metrics?client_id=${clientId}&date_from=${dateFrom}&date_to=${dateTo}&platform=meta`,{headers:h})
      .then(r=>setAds(r.data?.ads||[]))
      .catch(()=>setAds([]))
      .finally(()=>setAdsLoading(false));
  },[clientId,dateRange,tab]);

  const tabs=[
    {id:'meta',label:'📘 Facebook Ads',color:'#1877F2'},
    {id:'instagram',label:'📷 Instagram Ads',color:'#E1306C'},
    {id:'google',label:'🔴 Google Ads',color:'#EA4335'},
    {id:'creatives',label:'🎨 Creativos',color:'#8b5cf6'},
    {id:'compare',label:'📊 Comparación',color:'#6366f1'},
  ];

  if(loading) return <div style={{padding:60,textAlign:'center',color:'#94a3b8'}}>Cargando campañas...</div>;

  const totalSpent=(meta?.spent||0)+(google?.spent||0);

  return (
    <div>
      <Tabs tabs={tabs} active={tab} onSelect={setTab}/>

      {tab==='meta'&&(
        <div>
          <SectionHeader title="Facebook Ads" subtitle="Rendimiento de campañas pagadas en Facebook" color="#1877F2" icon="📘"/>
          {meta?(
            <>
              <KpiGrid kpis={[
                {label:'Gasto',value:meta.spent,type:'usd',color:'#1877F2'},
                {label:'Impresiones',value:meta.impressions,color:'#1877F2'},
                {label:'Clicks',value:meta.clicks,color:'#1877F2'},
                {label:'CTR',value:meta.ctr,type:'pct',color:'#1877F2'},
                {label:'CPC',value:meta.cpc,type:'usd',color:'#1877F2'},
                {label:'Conversiones',value:meta.conversions,color:'#1877F2'},
                {label:'Alcance',value:meta.reach,color:'#1877F2'},
              ]}/>
              {metaTrend.length>0&&(
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
                  <TrendChart data={metaTrend} lines={[{key:'spent',name:'Gasto',color:'#1877F2'}]} title="Gasto diario"/>
                  <TrendChart data={metaTrend} lines={[{key:'clicks',name:'Clicks',color:'#2DC98A'},{key:'conversions',name:'Conversiones',color:'#FFB347'}]} title="Clicks y Conversiones"/>
                </div>
              )}
              <Card>
                <div style={{fontWeight:700,fontSize:13,color:'#1e293b',marginBottom:14}}>Campañas activas</div>
                <CampaignTable campaigns={metaCamps} color="#1877F2"/>
              </Card>
            </>
          ):<NoData msg="Sin datos de Facebook Ads para este período"/>}
        </div>
      )}

      {tab==='instagram'&&(
        <div>
          <SectionHeader title="Instagram Ads" subtitle="Rendimiento de campañas pagadas en Instagram" color="#E1306C" icon="📷"/>
          {meta?(
            <>
              <KpiGrid kpis={[
                {label:'Gasto',value:meta.spent,type:'usd',color:'#E1306C'},
                {label:'Alcance',value:meta.reach,color:'#E1306C'},
                {label:'Clicks',value:meta.clicks,color:'#E1306C'},
                {label:'CTR',value:meta.ctr,type:'pct',color:'#E1306C'},
                {label:'CPC',value:meta.cpc,type:'usd',color:'#E1306C'},
                {label:'Conversiones',value:meta.conversions,color:'#E1306C'},
              ]}/>
              {metaTrend.length>0&&(
                <TrendChart data={metaTrend} lines={[{key:'spent',name:'Gasto',color:'#E1306C'},{key:'clicks',name:'Clicks',color:'#A78BFA'}]} title="Tendencia"/>
              )}
              <Card>
                <div style={{fontWeight:700,fontSize:13,color:'#1e293b',marginBottom:14}}>Campañas</div>
                <CampaignTable campaigns={metaCamps} color="#E1306C"/>
              </Card>
            </>
          ):<NoData msg="Sin datos de Instagram Ads para este período"/>}
        </div>
      )}

      {tab==='google'&&(
        <div>
          <SectionHeader title="Google Ads" subtitle="Campañas de búsqueda y display" color="#EA4335" icon="🔴"/>
          {google?(
            <>
              <KpiGrid kpis={[
                {label:'Gasto',value:google.spent,type:'usd',color:'#EA4335'},
                {label:'Impresiones',value:google.impressions,color:'#EA4335'},
                {label:'Clicks',value:google.clicks,color:'#EA4335'},
                {label:'CTR',value:google.ctr,type:'pct',color:'#EA4335'},
                {label:'CPC',value:google.cpc,type:'usd',color:'#EA4335'},
                {label:'Conversiones',value:google.conversions,color:'#EA4335'},
              ]}/>
              {googleTrend.length>0&&(
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
                  <TrendChart data={googleTrend} lines={[{key:'spent',name:'Gasto',color:'#EA4335'}]} title="Gasto diario"/>
                  <TrendChart data={googleTrend} lines={[{key:'clicks',name:'Clicks',color:'#2DC98A'},{key:'conversions',name:'Conversiones',color:'#FFB347'}]} title="Clicks y Conversiones"/>
                </div>
              )}
              <Card>
                <div style={{fontWeight:700,fontSize:13,color:'#1e293b',marginBottom:14}}>Campañas activas</div>
                <CampaignTable campaigns={googleCamps} color="#EA4335"/>
              </Card>
            </>
          ):<NoData msg="Sin datos de Google Ads para este período"/>}
        </div>
      )}

      {tab==='creatives'&&(
        <div>
          <SectionHeader title="Creativos y Anuncios" subtitle="Vista de ads con métricas de rendimiento" color="#8b5cf6" icon="🎨"/>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div style={{fontSize:13,color:'#64748b'}}>{adsLoading?'Cargando...':`${ads.length} anuncios`}</div>
            <div style={{display:'flex',gap:6}}>
              <button onClick={()=>setViewMode('cards')} style={{padding:'6px 12px',borderRadius:8,border:'1px solid #e2e8f0',background:viewMode==='cards'?'#eff6ff':'#fff',color:viewMode==='cards'?'#2563eb':'#64748b',fontWeight:600,fontSize:12,cursor:'pointer'}}>⊞ Cards</button>
              <button onClick={()=>setViewMode('table')} style={{padding:'6px 12px',borderRadius:8,border:'1px solid #e2e8f0',background:viewMode==='table'?'#eff6ff':'#fff',color:viewMode==='table'?'#2563eb':'#64748b',fontWeight:600,fontSize:12,cursor:'pointer'}}>☰ Tabla</button>
            </div>
          </div>
          {adsLoading?<Card><div style={{textAlign:'center',padding:40,color:'#94a3b8'}}>Cargando creativos...</div></Card>:
          viewMode==='cards'?(
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:16}}>
              {ads.map((ad,i)=>(
                <div key={i} style={{background:'#fff',borderRadius:14,overflow:'hidden',border:'1px solid #eef2f7',transition:'all 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
                  <div style={{position:'relative',height:180,background:'#f8fafc',overflow:'hidden'}}>
                    {ad.thumbnail_url?<img src={ad.thumbnail_url} alt={ad.ad_name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',fontSize:40}}>🖼</div>}
                    <div style={{position:'absolute',top:8,right:8,padding:'3px 8px',borderRadius:20,fontSize:11,fontWeight:700,background:ad.ad_status==='ACTIVE'?'#10b981':'#94a3b8',color:'#fff'}}>{ad.ad_status==='ACTIVE'?'Activo':'Pausado'}</div>
                  </div>
                  <div style={{padding:'12px 14px'}}>
                    <div style={{fontWeight:700,fontSize:13,color:'#1e293b',marginBottom:4,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ad.ad_name}</div>
                    <div style={{fontSize:11,color:'#94a3b8',marginBottom:10,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ad.campaign_name}</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                      {[{l:'Gasto',v:ad.spent,t:'usd',c:'#8b5cf6'},{l:'Clicks',v:ad.clicks,t:'num',c:'#3b82f6'},{l:'CTR',v:ad.ctr,t:'pct',c:'#10b981'},{l:'CPC',v:ad.cpc,t:'usd',c:'#f59e0b'}].map((m,j)=>(
                        <div key={j} style={{background:'#f8fafc',borderRadius:8,padding:'8px 10px'}}>
                          <div style={{fontSize:10,color:'#94a3b8',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.05em'}}>{m.l}</div>
                          <div style={{fontSize:14,fontWeight:800,color:m.c,marginTop:2}}>{fmt(m.v,m.t)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ):(
            <Card>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead><tr style={{background:'#8b5cf610',borderBottom:'2px solid #8b5cf620'}}>
                    {['Ad','Campaña','Estado','Gasto','Clicks','CTR','CPC','Conv.'].map(h=><th key={h} style={{padding:'10px 14px',textAlign:h==='Ad'||h==='Campaña'||h==='Estado'?'left':'right',fontSize:11,fontWeight:700,color:'#8b5cf6',textTransform:'uppercase',letterSpacing:'0.06em',whiteSpace:'nowrap'}}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {ads.map((ad,i)=>(
                      <tr key={i} style={{background:i%2===0?'#fff':'#f8fafc',borderBottom:'1px solid #f1f5f9'}}>
                        <td style={{padding:'10px 14px',display:'flex',alignItems:'center',gap:10}}>
                          {ad.thumbnail_url?<img src={ad.thumbnail_url} alt="" style={{width:36,height:36,borderRadius:6,objectFit:'cover',flexShrink:0}}/>:<div style={{width:36,height:36,borderRadius:6,background:'#f1f5f9',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>🖼</div>}
                          <span style={{fontWeight:600,color:'#1e293b',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ad.ad_name}</span>
                        </td>
                        <td style={{padding:'10px 14px',color:'#64748b',maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ad.campaign_name}</td>
                        <td style={{padding:'10px 14px'}}><span style={{padding:'3px 8px',borderRadius:20,fontSize:11,fontWeight:700,background:ad.ad_status==='ACTIVE'?'#d1fae5':'#f1f5f9',color:ad.ad_status==='ACTIVE'?'#059669':'#64748b'}}>{ad.ad_status==='ACTIVE'?'Activo':'Pausado'}</span></td>
                        <td style={{padding:'10px 14px',textAlign:'right',fontWeight:600,color:'#8b5cf6'}}>{fmt(ad.spent,'usd')}</td>
                        <td style={{padding:'10px 14px',textAlign:'right',color:'#475569'}}>{fmt(ad.clicks)}</td>
                        <td style={{padding:'10px 14px',textAlign:'right',color:'#475569'}}>{fmt(ad.ctr,'pct')}</td>
                        <td style={{padding:'10px 14px',textAlign:'right',color:'#475569'}}>{fmt(ad.cpc,'usd')}</td>
                        <td style={{padding:'10px 14px',textAlign:'right',color:'#475569'}}>{fmt(ad.conversions)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {tab==='compare'&&(
        <div>
          <SectionHeader title="Comparación entre canales" subtitle="Meta Ads vs Google Ads" color="#6366f1" icon="📊"/>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
            <Card>
              <div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Distribución de inversión</div>
              {totalSpent>0?(
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={[{name:'Meta Ads',value:meta?.spent||0},{name:'Google Ads',value:google?.spent||0}].filter(d=>d.value>0)} dataKey="value" cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3}>
                      {[0,1].map(i=><Cell key={i} fill={COLORS[i]} strokeWidth={0}/>)}
                    </Pie>
                    <Tooltip formatter={v=>fmt(v,'usd')} contentStyle={TIP}/>
                    <Legend/>
                  </PieChart>
                </ResponsiveContainer>
              ):<div style={{textAlign:'center',color:'#94a3b8',padding:40}}>Sin datos</div>}
            </Card>
            <Card>
              <div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Métricas comparadas</div>
              <table style={{width:'100%',fontSize:13,borderCollapse:'collapse'}}>
                <thead><tr style={{borderBottom:'2px solid #f1f5f9'}}>
                  <th style={{textAlign:'left',padding:'8px 0',color:'#94a3b8',fontSize:11,fontWeight:700}}>MÉTRICA</th>
                  <th style={{textAlign:'right',padding:'8px 0',color:'#1877F2',fontSize:11,fontWeight:700}}>META</th>
                  <th style={{textAlign:'right',padding:'8px 0',color:'#EA4335',fontSize:11,fontWeight:700}}>GOOGLE</th>
                </tr></thead>
                <tbody>
                  {[{l:'Gasto',k:'spent',t:'usd'},{l:'Clicks',k:'clicks',t:'num'},{l:'CTR',k:'ctr',t:'pct'},{l:'CPC',k:'cpc',t:'usd'},{l:'Conversiones',k:'conversions',t:'num'}].map((r,i)=>(
                    <tr key={i} style={{borderBottom:'1px solid #f8fafc'}}>
                      <td style={{padding:'9px 0',color:'#475569'}}>{r.l}</td>
                      <td style={{padding:'9px 0',textAlign:'right',fontWeight:600,color:'#1877F2'}}>{fmt(meta?.[r.k],r.t)}</td>
                      <td style={{padding:'9px 0',textAlign:'right',fontWeight:600,color:'#EA4335'}}>{fmt(google?.[r.k],r.t)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
          {(metaTrend.length>0||googleTrend.length>0)&&(
            <Card>
              <div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Gasto diario por canal</div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart margin={{top:4,right:4,bottom:0,left:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false}/>
                  <XAxis dataKey="date_label" tick={TICK} axisLine={false} tickLine={false}/>
                  <YAxis tick={TICK} axisLine={false} tickLine={false} width={40} tickFormatter={fmtTick}/>
                  <Tooltip contentStyle={TIP}/>
                  <Legend wrapperStyle={{fontSize:12}}/>
                  {metaTrend.length>0&&<Line data={metaTrend} type="monotone" dataKey="spent" name="Meta" stroke="#1877F2" strokeWidth={2.5} dot={false}/>}
                  {googleTrend.length>0&&<Line data={googleTrend} type="monotone" dataKey="spent" name="Google" stroke="#EA4335" strokeWidth={2.5} dot={false}/>}
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ── SOCIAL ORGÁNICO ───────────────────────────────────────────────────────────
function SocialOrganicReport({clientId,dateRange}) {
  const [fb,setFb]=useState(null);
  const [ig,setIg]=useState(null);
  const [loading,setLoading]=useState(true);
  const [tab,setTab]=useState('facebook');

  useEffect(()=>{
    if(!clientId)return;
    setLoading(true);
    const h={Authorization:`Bearer ${getToken()}`};
    const {dateFrom,dateTo}=getDates(dateRange);
    Promise.all([
      axios.get(`${API}/organic/facebook?client_id=${clientId}&date_from=${dateFrom}&date_to=${dateTo}`,{headers:h}).catch(()=>({data:null})),
      axios.get(`${API}/organic/instagram?client_id=${clientId}&date_from=${dateFrom}&date_to=${dateTo}`,{headers:h}).catch(()=>({data:null})),
    ]).then(([fbRes,igRes])=>{setFb(fbRes.data);setIg(igRes.data);}).finally(()=>setLoading(false));
  },[clientId,dateRange]);

  const tabs=[
    {id:'facebook',label:'📘 Facebook Page',color:'#1877F2'},
    {id:'instagram',label:'📷 Instagram',color:'#E1306C'},
  ];

  const PostTable=({posts,color})=>{
    if(!posts?.length) return <div style={{padding:32,textAlign:'center',color:'#94a3b8',fontSize:13}}>Sin posts en este período</div>;
    return (
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead><tr style={{background:color+'10',borderBottom:`2px solid ${color}20`}}>
            {['Post','Fecha','Likes','Comentarios','Alcance'].map(h=><th key={h} style={{padding:'10px 14px',textAlign:h==='Post'?'left':'right',fontSize:11,fontWeight:700,color,textTransform:'uppercase',letterSpacing:'0.06em'}}>{h}</th>)}
          </tr></thead>
          <tbody>
            {posts.slice(0,15).map((p,i)=>(
              <tr key={i} style={{background:i%2===0?'#fff':'#f8fafc',borderBottom:'1px solid #f1f5f9'}}>
                <td style={{padding:'10px 14px',maxWidth:260,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'#1e293b',fontWeight:500}}>{p.message||p.caption||'Sin texto'}</td>
                <td style={{padding:'10px 14px',textAlign:'right',color:'#64748b'}}>{(p.created_time||p.timestamp||'').slice(0,10)||'—'}</td>
                <td style={{padding:'10px 14px',textAlign:'right',color:'#475569'}}>{fmt(p.likes||p.like_count)}</td>
                <td style={{padding:'10px 14px',textAlign:'right',color:'#475569'}}>{fmt(p.comments||p.comments_count)}</td>
                <td style={{padding:'10px 14px',textAlign:'right',color:'#475569'}}>{fmt(p.reach)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if(loading) return <div style={{padding:60,textAlign:'center',color:'#94a3b8'}}>Cargando datos orgánicos...</div>;

  return (
    <div>
      <Tabs tabs={tabs} active={tab} onSelect={setTab}/>
      {tab==='facebook'&&(
        <div>
          <SectionHeader title="Facebook Page" subtitle="Contenido orgánico y audiencia" color="#1877F2" icon="📘"/>
          {fb?.insights?(
            <>
              <KpiGrid kpis={[
                {label:'Alcance',value:fb.insights?.page_impressions_unique?.total||fb.insights?.reach?.total,color:'#1877F2'},
                {label:'Impresiones',value:fb.insights?.page_impressions?.total,color:'#1877F2'},
                {label:'Seguidores',value:fb.insights?.page_fans?.total||fb.page?.fan_count,color:'#1877F2'},
                {label:'Engagement',value:fb.insights?.page_engaged_users?.total,color:'#1877F2'},
                {label:'Vistas',value:fb.insights?.page_views_total?.total,color:'#1877F2'},
              ].filter(k=>k.value!=null)}/>
              <Card><div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Posts recientes</div><PostTable posts={fb.posts} color="#1877F2"/></Card>
            </>
          ):<NoData msg={fb?.error||'Sin datos de Facebook Page. Verificar conexión en Configuración.'}/>}
        </div>
      )}
      {tab==='instagram'&&(
        <div>
          <SectionHeader title="Instagram Orgánico" subtitle="Contenido y audiencia" color="#E1306C" icon="📷"/>
          {ig?.insights?(
            <>
              <KpiGrid kpis={[
                {label:'Alcance',value:ig.insights?.reach?.total,color:'#E1306C'},
                {label:'Impresiones',value:ig.insights?.impressions?.total,color:'#E1306C'},
                {label:'Seguidores',value:ig.insights?.follower_count?.total,color:'#E1306C'},
                {label:'Visitas Perfil',value:ig.insights?.profile_views?.total,color:'#E1306C'},
              ].filter(k=>k.value!=null)}/>
              <Card><div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Posts recientes</div><PostTable posts={ig.media} color="#E1306C"/></Card>
            </>
          ):<NoData msg={ig?.error||'Sin datos de Instagram. Verificar conexión en Configuración.'}/>}
        </div>
      )}
    </div>
  );
}

// ── SEO & WEB ─────────────────────────────────────────────────────────────────
function SeoWebReport({clientId,dateRange}) {
  const [ga4,setGa4]=useState(null);
  const [gsc,setGsc]=useState(null);
  const [ga4Trend,setGa4Trend]=useState([]);
  const [loading,setLoading]=useState(true);
  const [tab,setTab]=useState('gsc');

  useEffect(()=>{
    if(!clientId)return;
    setLoading(true);
    const h={Authorization:`Bearer ${getToken()}`};
    const {dateFrom,dateTo}=getDates(dateRange);
    Promise.all([
      axios.get(`${API}/ga4/metrics?client_id=${clientId}&date_from=${dateFrom}&date_to=${dateTo}`,{headers:h}).catch(()=>({data:null})),
      axios.get(`${API}/gsc/metrics?date_from=${dateFrom}&date_to=${dateTo}`,{headers:h}).catch(()=>({data:null})),
      axios.get(`${API}/extension/ga4-stats?client_id=${clientId}&days=30`,{headers:h}).catch(()=>({data:null})),
      axios.get(`${API}/extension/ga4-timeseries?client_id=${clientId}&range=${dateRange}`,{headers:h}).catch(()=>({data:null})),
    ]).then(([ga4Res,gscRes,ga4StatsRes,ga4TsRes])=>{
      const base=ga4Res.data?.connected?ga4Res.data:null;
      const stats=ga4StatsRes.data?.connected?ga4StatsRes.data:null;
      setGa4(base?{...base,top_pages:stats?.pages||[],channels:stats?.channels||[],countries:stats?.countries||[]}:null);
      setGsc(gscRes.data?.connected?gscRes.data:null);
      setGa4Trend(ga4TsRes.data?.days||ga4TsRes.data?.data||[]);
    }).finally(()=>setLoading(false));
  },[clientId,dateRange]);

  const tabs=[
    {id:'gsc',label:'🔍 Search Console',color:'#4285F4'},
    {id:'ga4',label:'📊 Google Analytics',color:'#F9AB00'},
  ];

  if(loading) return <div style={{padding:60,textAlign:'center',color:'#94a3b8'}}>Cargando datos SEO...</div>;

  return (
    <div>
      <Tabs tabs={tabs} active={tab} onSelect={setTab}/>
      {tab==='gsc'&&(
        <div>
          <SectionHeader title="Google Search Console" subtitle="Visibilidad y clicks orgánicos" color="#4285F4" icon="🔍"/>
          {gsc?(
            <KpiGrid kpis={[
              {label:'Clicks Orgánicos',value:gsc.clicks,color:'#4285F4'},
              {label:'Impresiones',value:gsc.impressions,color:'#4285F4'},
              {label:'CTR',value:gsc.ctr,type:'pct',color:'#4285F4'},
              {label:'Posición Promedio',value:gsc.position,type:'dec',color:'#4285F4'},
            ]}/>
          ):<NoData msg="Sin datos de Search Console. Verificar conexión."/>}
        </div>
      )}
      {tab==='ga4'&&(
        <div>
          <SectionHeader title="Google Analytics 4" subtitle="Tráfico y comportamiento del sitio" color="#F9AB00" icon="📊"/>
          {ga4?(
            <>
              <KpiGrid kpis={[
                {label:'Usuarios',value:ga4.users,color:'#F9AB00'},
                {label:'Sesiones',value:ga4.sessions,color:'#F9AB00'},
                {label:'Pageviews',value:ga4.pageviews,color:'#F9AB00'},
                {label:'Tasa de Rebote',value:ga4.bounce_rate,type:'pct',color:'#F9AB00'},
                {label:'Conversiones',value:ga4.conversions,color:'#F9AB00'},
              ]}/>
              {ga4Trend.length>0&&(
                <TrendChart data={ga4Trend} lines={[{key:'sessions',name:'Sesiones',color:'#F9AB00'},{key:'users',name:'Usuarios',color:'#4285F4'}]} title="Sesiones y usuarios por día"/>
              )}
              {ga4.top_pages?.length>0&&(
                <Card>
                  <div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Top páginas</div>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                    <thead><tr style={{background:'#F9AB0010',borderBottom:'2px solid #F9AB0020'}}>
                      {['Página','Visitas'].map(h=><th key={h} style={{padding:'10px 14px',textAlign:h==='Página'?'left':'right',fontSize:11,fontWeight:700,color:'#F9AB00',textTransform:'uppercase',letterSpacing:'0.06em'}}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {ga4.top_pages.slice(0,15).map((p,i)=>(
                        <tr key={i} style={{background:i%2===0?'#fff':'#f8fafc',borderBottom:'1px solid #f1f5f9'}}>
                          <td style={{padding:'9px 14px',color:'#1e293b',fontWeight:500}}>{p.title||p.page}</td>
                          <td style={{padding:'9px 14px',textAlign:'right',color:'#475569',fontWeight:600}}>{fmt(p.views||p.sessions)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
              {ga4.channels?.length>0&&(
                <Card>
                  <div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Fuentes de tráfico</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={ga4.channels} margin={{top:4,right:4,bottom:0,left:0}}>
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false}/>
                      <XAxis dataKey="channel" tick={TICK} axisLine={false} tickLine={false}/>
                      <YAxis tick={TICK} axisLine={false} tickLine={false} width={40} tickFormatter={fmtTick}/>
                      <Tooltip contentStyle={TIP}/>
                      <Bar dataKey="sessions" name="Sesiones" radius={[4,4,0,0]}>
                        {ga4.channels.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              )}
            </>
          ):<NoData msg="Sin datos de Google Analytics. Verificar conexión."/>}
        </div>
      )}
    </div>
  );
}

// ── EJECUTIVO ─────────────────────────────────────────────────────────────────
function ExecutiveReport({clientId,dateRange}) {
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);

  useEffect(()=>{
    if(!clientId)return;
    setLoading(true);
    const h={Authorization:`Bearer ${getToken()}`};
    const {dateFrom,dateTo}=getDates(dateRange);
    const metaEp=(dateRange==='this_month'||dateRange==='last_month')
      ?`${API}/campaigns?client_id=${clientId}&platform=meta&date_from=${dateFrom}&date_to=${dateTo}`
      :`${API}/meta/campaigns-live?client_id=${clientId}&date_from=${dateFrom}&date_to=${dateTo}`;
    Promise.all([
      axios.get(metaEp,{headers:h}).catch(()=>({data:null})),
      axios.get(`${API}/campaigns?client_id=${clientId}&platform=google&date_from=${dateFrom}&date_to=${dateTo}`,{headers:h}).catch(()=>({data:[]})),
      axios.get(`${API}/ga4/metrics?client_id=${clientId}&date_from=${dateFrom}&date_to=${dateTo}`,{headers:h}).catch(()=>({data:null})),
      axios.get(`${API}/gsc/metrics?date_from=${dateFrom}&date_to=${dateTo}`,{headers:h}).catch(()=>({data:null})),
    ]).then(([mRes,gRes,ga4Res,gscRes])=>{
      const mc=Array.isArray(mRes.data)?mRes.data:(mRes.data?.campaigns||[]);
      const gc=Array.isArray(gRes.data)?gRes.data:(gRes.data?.campaigns||[]);
      const sum=(a,k)=>a.reduce((s,c)=>s+(parseFloat(c[k])||0),0);
      setData({
        meta:mc.length?{spent:sum(mc,'spent'),conversions:sum(mc,'conversions'),clicks:sum(mc,'clicks')}:null,
        google:gc.length?{spent:sum(gc,'spent'),conversions:sum(gc,'conversions'),clicks:sum(gc,'clicks')}:null,
        ga4:ga4Res.data?.connected?ga4Res.data:null,
        gsc:gscRes.data?.connected?gscRes.data:null,
      });
    }).finally(()=>setLoading(false));
  },[clientId,dateRange]);

  if(loading) return <div style={{padding:60,textAlign:'center',color:'#94a3b8'}}>Cargando reporte ejecutivo...</div>;

  const totalSpent=(data?.meta?.spent||0)+(data?.google?.spent||0);
  const totalConv=(data?.meta?.conversions||0)+(data?.google?.conversions||0);

  const channels=[
    data?.meta&&{name:'Meta Ads',spent:data.meta.spent,conv:data.meta.conversions,clicks:data.meta.clicks,color:'#1877F2',icon:'📘'},
    data?.google&&{name:'Google Ads',spent:data.google.spent,conv:data.google.conversions,clicks:data.google.clicks,color:'#EA4335',icon:'🔴'},
    data?.gsc&&{name:'SEO Orgánico',clicks:data.gsc.clicks,impressions:data.gsc.impressions,color:'#34A853',icon:'🔍'},
    data?.ga4&&{name:'Tráfico Web',sessions:data.ga4.sessions,users:data.ga4.users,color:'#F9AB00',icon:'📊'},
  ].filter(Boolean);

  const distData=[
    {name:'Meta Ads',value:data?.meta?.spent||0},
    {name:'Google Ads',value:data?.google?.spent||0},
  ].filter(d=>d.value>0);

  return (
    <div>
      <SectionHeader title="Reporte Ejecutivo" subtitle="Visión global de todos los canales" color="#6366f1" icon="📈"/>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:20}}>
        {[
          {label:'Inversión Total',value:totalSpent,type:'usd',color:'#6366f1',icon:'💰'},
          {label:'Conversiones',value:totalConv,color:'#10b981',icon:'🎯'},
          {label:'Clicks Pagados',value:(data?.meta?.clicks||0)+(data?.google?.clicks||0),color:'#f59e0b',icon:'👆'},
          {label:'Clicks Orgánicos',value:data?.gsc?.clicks,color:'#3b82f6',icon:'🌱'},
        ].map((k,i)=>(
          <div key={i} style={{background:'#fff',borderRadius:12,padding:'20px',border:'1px solid #eef2f7',borderTop:`3px solid ${k.color}`,textAlign:'center'}}>
            <div style={{fontSize:28,marginBottom:8}}>{k.icon}</div>
            <div style={{fontSize:24,fontWeight:900,color:k.color}}>{fmt(k.value,k.type)}</div>
            <div style={{fontSize:11,color:'#94a3b8',marginTop:4,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.06em'}}>{k.label}</div>
          </div>
        ))}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 2fr',gap:16,marginBottom:16}}>
        <Card>
          <div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Inversión por canal</div>
          {distData.length>0?(
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={distData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3}>
                  {distData.map((_,i)=><Cell key={i} fill={COLORS[i]} strokeWidth={0}/>)}
                </Pie>
                <Tooltip formatter={v=>fmt(v,'usd')} contentStyle={TIP}/>
                <Legend/>
              </PieChart>
            </ResponsiveContainer>
          ):<div style={{textAlign:'center',color:'#94a3b8',padding:40}}>Sin datos</div>}
        </Card>
        <Card>
          <div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Resumen por canal</div>
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {channels.map((ch,i)=>(
              <div key={i} style={{display:'flex',alignItems:'center',gap:14,padding:'12px 14px',background:'#f8fafc',borderRadius:10,border:`1px solid ${ch.color}20`}}>
                <span style={{fontSize:18}}>{ch.icon}</span>
                <div style={{fontWeight:700,color:'#1e293b',minWidth:150,fontSize:13}}>{ch.name}</div>
                <div style={{display:'flex',gap:20,flex:1}}>
                  {ch.spent!=null&&<div><div style={{fontSize:10,color:'#94a3b8',marginBottom:1}}>Gasto</div><div style={{fontWeight:700,color:ch.color,fontSize:13}}>{fmt(ch.spent,'usd')}</div></div>}
                  {ch.conv!=null&&<div><div style={{fontSize:10,color:'#94a3b8',marginBottom:1}}>Conversiones</div><div style={{fontWeight:700,color:'#1e293b',fontSize:13}}>{fmt(ch.conv)}</div></div>}
                  {ch.clicks!=null&&<div><div style={{fontSize:10,color:'#94a3b8',marginBottom:1}}>Clicks</div><div style={{fontWeight:700,color:'#1e293b',fontSize:13}}>{fmt(ch.clicks)}</div></div>}
                  {ch.sessions!=null&&<div><div style={{fontSize:10,color:'#94a3b8',marginBottom:1}}>Sesiones</div><div style={{fontWeight:700,color:'#1e293b',fontSize:13}}>{fmt(ch.sessions)}</div></div>}
                </div>
                {totalSpent>0&&ch.spent>0&&<div style={{textAlign:'right',minWidth:55}}><div style={{fontSize:16,fontWeight:900,color:ch.color}}>{((ch.spent/totalSpent)*100).toFixed(0)}%</div><div style={{fontSize:10,color:'#94a3b8'}}>del gasto</div></div>}
              </div>
            ))}
            {channels.length===0&&<div style={{textAlign:'center',color:'#94a3b8',padding:40}}>Sin datos</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function MarketingStudio() {
  const {clientId}=useWorkspace();
  const {theme}=useTheme()||{theme:{}};
  const [activeReport,setActiveReport]=useState('paid');
  const [dateRange,setDateRange]=useState('30d');

  const reports=[
    {id:'paid',label:'💰 Campañas Pagadas'},
    {id:'organic',label:'🌱 Social Orgánico'},
    {id:'seo',label:'🔍 SEO & Web'},
    {id:'executive',label:'📈 Ejecutivo'},
  ];

  if(!clientId) return (
    <DashboardLayout>
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',flexDirection:'column',gap:12}}>
        <div style={{fontSize:48}}>👈</div>
        <div style={{fontSize:16,fontWeight:600,color:'#1e293b'}}>Selecciona un cliente para ver los reportes</div>
        <div style={{fontSize:13,color:'#94a3b8'}}>Usa el selector de workspace en la barra lateral</div>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div style={{display:'flex',flexDirection:'column',height:'100vh',background:'#f4f7fb',fontFamily:"'Inter',-apple-system,sans-serif"}}>
        <div style={{background:'#fff',borderBottom:'1px solid #e8edf4',padding:'0 24px',height:54,display:'flex',alignItems:'center',gap:16,flexShrink:0,boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
          <span style={{fontWeight:800,fontSize:15,color:'#0f172a'}}>Marketing Studio</span>
          <div style={{width:1,height:18,background:'#e2e8f0'}}/>
          <div style={{display:'flex',gap:2}}>
            {reports.map(r=>(
              <button key={r.id} onClick={()=>setActiveReport(r.id)} style={{
                padding:'6px 14px',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',border:'none',transition:'all 0.15s',
                background:activeReport===r.id?'#eff6ff':'transparent',
                color:activeReport===r.id?'#2563eb':'#64748b',
              }}>{r.label}</button>
            ))}
          </div>
          <div style={{marginLeft:'auto'}}>
            <select value={dateRange} onChange={e=>setDateRange(e.target.value)} style={{
              fontSize:13,padding:'6px 12px',borderRadius:8,border:'1px solid #e2e8f0',
              background:'#fff',color:'#1e293b',fontWeight:600,cursor:'pointer',outline:'none'
            }}>
              {DATE_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:24}}>
          {activeReport==='paid'&&<PaidMediaReport key={`paid-${dateRange}-${clientId}`} clientId={clientId} dateRange={dateRange}/>}
          {activeReport==='organic'&&<SocialOrganicReport key={`organic-${dateRange}-${clientId}`} clientId={clientId} dateRange={dateRange}/>}
          {activeReport==='seo'&&<SeoWebReport key={`seo-${dateRange}-${clientId}`} clientId={clientId} dateRange={dateRange}/>}
          {activeReport==='executive'&&<ExecutiveReport key={`exec-${dateRange}-${clientId}`} clientId={clientId} dateRange={dateRange}/>}
        </div>
      </div>
    </DashboardLayout>
  );
}
